import Cookies from 'js-cookie';
import React, { useContext, useEffect, useState } from 'react';
import { IconContext } from 'react-icons';

import {
  FaArrowCircleDown,
  FaChevronLeft,
  FaChevronRight,
  FaCompass,
  FaEye,
  FaWind,
} from 'react-icons/fa';

import { ConditionIcons } from 'components';
import { xml } from 'd3';
import { useAPIData } from 'hooks/useAPIData';
import { FaHouseChimney } from 'react-icons/fa6';
import { RiDropLine, RiTempHotFill } from 'react-icons/ri';
import { WiSprinkle } from 'react-icons/wi';
import { Range } from 'utils/mathUtils';
import OvalDisplay from '../OvalDisplay';
import { ErrorMessage } from './ErrorMessage';
import WidgetLoadingAnimation from './WidgetLoadingAnimation';
import WidgetTimeUpdate from './WidgetTimeUpdate';

import { ColorContext, LayoutSizeContext, UniqueWidgetRefreshContext } from 'App';
import { getHourlyForecast } from 'services/weather/weather.service';

let dataCache;

export default function WeatherForecastWidget({
  widgetRefresh,
  setWidgetRefresh,
  dimensions,
  containerSizePx,
}) {
  const layout = useContext(LayoutSizeContext);
  const [selectedItem, setSelectedItem] = useState(0);
  const [forecastHourlyData, setForecastHourlyData] = useState();
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState({ state: false, message: '' });
  const uwf = useContext(UniqueWidgetRefreshContext);
  const ROUTE = 'getHourlyForecast';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;
  const theme = useContext(ColorContext);
  const containerWidth = Number.isFinite(containerSizePx?.width)
    ? containerSizePx.width
    : layout.widgetWidth * Math.max(1, Number(dimensions?.[0]) || 1);
  const isSingleColumnWidget = dimensions?.[0] === 1;
  const arrowButtonWidth = Math.max(
    30,
    Math.min(50, Math.round(containerWidth * (isSingleColumnWidget ? 0.11 : 0.065))),
  );
  const arrowIconSize = Math.max(14, Math.min(20, Math.round(arrowButtonWidth * 0.42)));
  const compactWeather1x1VisibleCount = containerWidth < 310 ? 2 : 3;
  const compactWeather1x2Columns = containerWidth < 340 ? 2 : containerWidth < 440 ? 3 : 4;

  useEffect(() => {
    const fetchData = async () => {
      try {
        FusionData.forceGet({
          key: ROUTE,
          apiFunction: getHourlyForecast,
        }).then((res) => {
          let filteredData = res.data.result.map((item) => {
            return {
              ...item,
              local_time: parseDateTime(item.local_time.toString()),
            };
          });
          dataCache = filteredData;
          setForecastHourlyData(dataCache);
          setFetching(false);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [uwf[UNIQUEKEY]]);

  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });

  useEffect(() => {
    dataCache = null;
    setFetching(true);
  }, [widgetRefresh]);

  useEffect(() => {
    (async () => {
      if (dataCache) {
        setForecastHourlyData(dataCache);
        setFetching(false);
        return;
      }
      //get hourly forecast data
      FusionData.get({ key: ROUTE, apiFunction: getHourlyForecast })
        .then((res) => {
          let filteredData = res.data.result.map((item) => {
            return {
              ...item,
              local_time: parseDateTime(item.local_time.toString()),
            };
          });
          dataCache = filteredData;
          setForecastHourlyData(dataCache);
          setFetching(false);
        })
        .catch((err) => {
          setError({ state: true, message: err.message });
        });
    })();
  }, [widgetRefresh]);

  const parseDateTime = (datetime) => {
    const result = [];
    result.push(datetime[6], datetime[7], datetime[8], datetime[9]);
    result.push('-');
    result.push(datetime[2], datetime[3]);
    result.push('-');
    result.push(datetime[4], datetime[5]);
    result.push('T', datetime[0], datetime[1], ':00:00');

    return new Date(result.toString().replace(/,/g, ''));
  };

  const RightArrowComponent = () => {
    return (
      <button
        type="button"
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{
          width: arrowButtonWidth,
          minWidth: arrowButtonWidth,
          flex: `0 0 ${arrowButtonWidth}px`,
          border: 'none',
          background: 'transparent',
          padding: 0,
          opacity: selectedItem < forecastHourlyData.length - 1 ? 1 : 0,
          cursor: selectedItem < forecastHourlyData.length - 1 ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (selectedItem < forecastHourlyData.length - 1) setSelectedItem(selectedItem + 1);
        }}
      >
        <IconContext.Provider
          value={{
            color: theme.fontColor,
            size: arrowIconSize,
          }}
        >
          <FaChevronRight />
        </IconContext.Provider>
      </button>
    );
  };

  const LeftArrowComponent = () => {
    return (
      <button
        type="button"
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{
          width: arrowButtonWidth,
          minWidth: arrowButtonWidth,
          flex: `0 0 ${arrowButtonWidth}px`,
          border: 'none',
          background: 'transparent',
          padding: 0,
          opacity: selectedItem > 0 ? 1 : 0,
          cursor: selectedItem > 0 ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (selectedItem > 0) setSelectedItem(selectedItem - 1);
        }}
      >
        <IconContext.Provider
          value={{
            color: theme.fontColor,
            size: arrowIconSize,
          }}
        >
          <FaChevronLeft />
        </IconContext.Provider>
      </button>
    );
  };

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  if (fetching)
    return <WidgetLoadingAnimation widgetName={'WeatherForecast'} dimensions={dimensions} />;

  if (forecastHourlyData && forecastHourlyData.length === 0) {
    return (
      <div>
        <WidgetTimeUpdate route={ROUTE} updateIntervalMinutes={60} />
        <div className="p-3">No data found.</div>
      </div>
    );
  }

  if (dimensions[0] == 2 && dimensions[1] == 1)
    return (
      <div className="d-flex flex-column flex-grow-1  h-100 align-content-center justify-content-center overflow-x-hidden overflow-y-auto">
        <WidgetTimeUpdate route={ROUTE} />
        {forecastHourlyData && forecastHourlyData.length && (
          <div
            className="d-flex w-100 justify-content-center align-items-center"
            style={{ height: '90%' }}
          >
            <div className="w-100 h-100 d-flex flex-column justify-content-start mx-3 my-2">
              <div
                className="d-flex flex-row w-100 align-items-center justify-content-around gap-2 mt-2"
                style={{
                  height: '75%',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <LeftArrowComponent />

                {forecastHourlyData &&
                  forecastHourlyData.map((item, index) => {
                    const length = forecastHourlyData.length;
                    const numDisplayed = 7;
                    const halfNum = numDisplayed / 2;

                    if (
                      !Range(
                        Math.max(Math.min(selectedItem, length - halfNum), halfNum) - halfNum,
                        Math.min(Math.max(selectedItem, halfNum), length - halfNum) + halfNum,
                        index,
                      )
                    )
                      return;

                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setSelectedItem(index)}
                        className="h-100 p-0 border-0 bg-transparent"
                        style={{ flex: '1 1 0', minWidth: 0 }}
                      >
                        <OvalDisplay
                          time={
                            (item.local_time.getHours() <= 12
                              ? item.local_time.getHours() > 0
                                ? item.local_time.getHours()
                                : '12'
                              : item.local_time.getHours() - 12) +
                            ' ' +
                            (item.local_time.getHours() < 12 ? ' AM' : ' PM')
                          }
                          temp={item.temperature}
                          selected={index === selectedItem}
                          icon={<ConditionIcons conditionId={parseInt(item.icon)} />}
                          textStyle={{ fontSize: layout.baseFontSize, color: theme.fontColor }}
                        />
                      </button>
                    );
                  })}

                <RightArrowComponent />
              </div>
              {forecastHourlyData && (
                <div
                  className="d-flex w-100 align-items-center justify-content-around"
                  style={{ height: '35%', marginLeft: '8%', fontSize: layout.baseFontSize }}
                >
                  <div className="flex-grow-1 h-100">
                    <div className="my-2">
                      <RiTempHotFill />
                      {` Temperature: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].temperature) ||
                        '?'
                      }°F`}
                    </div>
                    <div className="my-2">
                      <FaHouseChimney />
                      {` Comfort Level: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].comfort) ||
                        '?'
                      }°F`}
                    </div>
                    <div className="my-2">
                      <WiSprinkle />
                      {` Dew Point: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].dew_point) ||
                        '?'
                      }°F`}
                    </div>
                  </div>
                  <div className="flex-grow-1 h-100">
                    <div className="my-2">
                      <FaWind />
                      {` Wind SP: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].wind_speed) ||
                        '?'
                      } MPH`}
                    </div>
                    <div className="my-2">
                      <FaCompass />
                      {` Wind Direction: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].wind_dir) ||
                        '?'
                      }°`}
                    </div>
                  </div>
                  <div className="flex-grow-1 h-100">
                    <div className="my-2">
                      <RiDropLine />
                      {` Humidity: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].humidity) ||
                        '?'
                      }%`}
                    </div>
                    <div className="my-2">
                      <FaEye />
                      {` Visibility: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].visibility) ||
                        '?'
                      } MI`}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );

  if (dimensions[0] == 1 && dimensions[1] == 2)
    return (
      <div className="d-flex flex-column flex-grow-1  h-100 align-content-center justify-content-center overflow-x-hidden overflow-y-auto">
        <WidgetTimeUpdate route={ROUTE} />
        {forecastHourlyData && forecastHourlyData.length && (
          <div className="d-flex w-100 h-100 align-items-center flex-column pt-2">
            <div
              className="d-flex flex-row w-100 h-50 align-items-center justify-content-between"
              style={{ minWidth: 0, overflow: 'hidden', gap: 4 }}
            >
              <LeftArrowComponent />

              <div
                className="d-grid flex-grow-1 h-100"
                style={{
                  rowGap: 5,
                  columnGap: 5,
                  minWidth: 0,
                  gridTemplateColumns: `repeat(${compactWeather1x2Columns}, minmax(0, 1fr))`,
                }}
              >
                {forecastHourlyData &&
                  forecastHourlyData.map((item, index) => {
                    const length = forecastHourlyData.length;
                    const numDisplayed = compactWeather1x2Columns * 2;
                    const halfNum = numDisplayed / 2;

                    if (
                      !Range(
                        Math.max(Math.min(selectedItem, length - halfNum), halfNum) - halfNum,
                        Math.min(Math.max(selectedItem, halfNum), length - halfNum) + halfNum,
                        index,
                      )
                    )
                      return;

                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setSelectedItem(index)}
                        className="w-100 h-100 p-0 border-0 bg-transparent"
                        style={{ minWidth: 0 }}
                      >
                        <OvalDisplay
                          time={
                            (item.local_time.getHours() <= 12
                              ? item.local_time.getHours() > 0
                                ? item.local_time.getHours()
                                : '12'
                              : item.local_time.getHours() - 12) +
                            ' ' +
                            (item.local_time.getHours() < 12 ? ' AM' : ' PM')
                          }
                          temp={item.temperature}
                          selected={index === selectedItem}
                          icon={<ConditionIcons conditionId={parseInt(item.icon)} />}
                          iconStyle={{
                            width: Math.max(34, Math.min(50, Math.round(containerWidth * 0.13))),
                            height: Math.max(34, Math.min(50, Math.round(containerWidth * 0.13))),
                          }}
                        />
                      </button>
                    );
                  })}
              </div>

              <RightArrowComponent />
            </div>
            {forecastHourlyData && (
              <div
                className="d-flex w-100 align-items-center justify-content-around mt-3 ms-4"
                style={{ height: 90, fontSize: layout.baseFontSize }}
              >
                <div className="flex-grow-1 h-100">
                  <div className="my-2">
                    <RiTempHotFill />
                    {` Temperature: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].temperature) ||
                      '?'
                    }°F`}
                  </div>
                  <div className="my-2">
                    <FaHouseChimney />
                    {` Comfort Level: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].comfort) ||
                      '?'
                    }°F`}
                  </div>
                  <div className="my-2">
                    <WiSprinkle />
                    {` Dew Point: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].dew_point) ||
                      '?'
                    }°F`}
                  </div>

                  <div className="my-2">
                    <FaWind />
                    {` Wind SP: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].wind_speed) ||
                      '?'
                    } MPH`}
                  </div>
                  <div className="my-2">
                    <FaCompass />
                    {` Wind Direction: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].wind_dir) ||
                      '?'
                    }°`}
                  </div>
                  <div className="my-2">
                    <RiDropLine />
                    {` Humidity: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].humidity) ||
                      '?'
                    }%`}
                  </div>
                  <div className="my-2">
                    <FaEye />
                    {` Visibility: ${
                      (forecastHourlyData[selectedItem] &&
                        forecastHourlyData[selectedItem].visibility) ||
                      '?'
                    } MI`}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );

  if (dimensions[0] == 1 && dimensions[1] == 1)
    return (
      <div className="w-100 h-100 overflow-x-hidden overflow-y-auto">
        <WidgetTimeUpdate route={ROUTE} />
        {forecastHourlyData && forecastHourlyData.length && (
          <div className="d-flex w-100 justify-content-center" style={{ height: '95%' }}>
            <div
              className="w-100 d-flex flex-column justify-content-between mx-3 my-0"
              style={{ height: '85%' }}
            >
              <div
                className="d-flex flex-row w-100 align-items-center justify-content-around gap-2"
                style={{ height: '80%' }}
              >
                <LeftArrowComponent />

                {forecastHourlyData &&
                  forecastHourlyData.map((item, index) => {
                    const length = forecastHourlyData.length;
                    const numDisplayed = compactWeather1x1VisibleCount;
                    const halfNum = numDisplayed / 2;

                    if (
                      !Range(
                        Math.max(Math.min(selectedItem, length - halfNum), halfNum) - halfNum,
                        Math.min(Math.max(selectedItem, halfNum), length - halfNum) + halfNum,
                        index,
                      )
                    )
                      return;

                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setSelectedItem(index)}
                        className="w-100 h-100 p-0 border-0 bg-transparent"
                        style={{ minWidth: 0 }}
                      >
                        <OvalDisplay
                          time={
                            (item.local_time.getHours() <= 12
                              ? item.local_time.getHours() > 0
                                ? item.local_time.getHours()
                                : '12'
                              : item.local_time.getHours() - 12) +
                            ' ' +
                            (item.local_time.getHours() < 12 ? ' AM' : ' PM')
                          }
                          temp={item.temperature}
                          selected={index === selectedItem}
                          icon={<ConditionIcons conditionId={parseInt(item.icon)} />}
                          textStyle={{ fontSize: layout.baseFontSize, color: theme.fontColor }}
                          iconStyle={{
                            width: Math.max(34, Math.min(50, Math.round(containerWidth * 0.13))),
                            height: Math.max(34, Math.min(50, Math.round(containerWidth * 0.13))),
                          }}
                        />
                      </button>
                    );
                  })}

                <RightArrowComponent />
              </div>
              {forecastHourlyData && (
                <div
                  className="d-flex w-100 align-items-center justify-content-around mt-2"
                  style={{ fontSize: layout.baseFontSize, marginLeft: '5%' }}
                >
                  <div className="flex-grow-1 h-100">
                    <div className="my-2 mb-0" style={{ marginTop: 5 * layout.scaleMult }}>
                      <RiTempHotFill />
                      {` Temperature: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].temperature) ||
                        '?'
                      }°F`}
                    </div>
                    <div className="my-2 mb-0" style={{ marginTop: 5 * layout.scaleMult }}>
                      <FaHouseChimney />
                      {` Comfort Level: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].comfort) ||
                        '?'
                      }°F`}
                    </div>
                    <div className="my-2 mb-0" style={{ marginTop: 5 * layout.scaleMult }}>
                      <WiSprinkle />
                      {` Dew Point: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].dew_point) ||
                        '?'
                      }°F`}
                    </div>
                  </div>
                  <div className="flex-grow-1 h-100">
                    <div className="my-2 mb-0" style={{ marginTop: 5 * layout.scaleMult }}>
                      <RiDropLine />
                      {` Humidity: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].humidity) ||
                        '?'
                      }%`}
                    </div>
                    <div className="my-2 mb-0" style={{ marginTop: 5 * layout.scaleMult }}>
                      <FaEye />
                      {` Visibility: ${
                        (forecastHourlyData[selectedItem] &&
                          forecastHourlyData[selectedItem].visibility) ||
                        '?'
                      } MI`}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
}
